<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.UI.5 — Resumen (canvas: Main): status tiles, the sign-in
// policy, recent activity, and the controls that are still to come.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import KaptionSecuritySection from '../components/KaptionSecuritySection.vue';
import KaptionSecuritySoonCard from '../components/KaptionSecuritySoonCard.vue';
import KaptionSecurityEnforceDialog from '../components/KaptionSecurityEnforceDialog.vue';
import { useKaptionSecurityText } from '../useKaptionSecurityText';

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const {
  overview,
  setOverview,
  settings,
  connection,
  domains,
  verifiedDomains,
  isConnected,
  twoStepAvailable,
} = useKaptionSecurity();
const { provider, domainsText, formatWhen, eventText, eventWho } =
  useKaptionSecurityText(overview);

const enforceRef = ref(null);
const busy = ref(false);

const policy = computed(() => settings.value.sign_in_policy || 'password');
const ssoReady = computed(
  () => isConnected.value && verifiedDomains.value.length > 0
);
const pendingDomain = computed(() =>
  domains.value.find(d => d.status !== 'verified')
);
const twoStep = computed(() => overview.value?.two_step);

const tiles = computed(() => {
  const list = [];
  let ssoNote = t('KAPTION_SECURITY.OVERVIEW.TILE_SSO_CONNECT');
  let ssoTone = 'text-n-slate-11';
  if (connection.value && !isConnected.value) {
    ssoNote = t('KAPTION_SECURITY.OVERVIEW.TILE_SSO_NOT_ENABLED');
    ssoTone = 'text-n-amber-11';
  } else if (policy.value === 'sso_required') {
    ssoNote = t('KAPTION_SECURITY.OVERVIEW.TILE_SSO_REQUIRED', {
      domains: domainsText.value,
    });
    ssoTone = 'text-n-teal-11';
  } else if (isConnected.value) {
    ssoNote = t('KAPTION_SECURITY.OVERVIEW.TILE_SSO_AVAILABLE');
    ssoTone = 'text-n-teal-11';
  }
  list.push({
    key: 'sso',
    label: t('KAPTION_SECURITY.OVERVIEW.TILE_SSO'),
    value: connection.value
      ? provider.value
      : t('KAPTION_SECURITY.OVERVIEW.TILE_SSO_NONE'),
    note: ssoNote,
    tone: ssoTone,
  });

  let domainNote = t('KAPTION_SECURITY.OVERVIEW.TILE_DOMAINS_ADD');
  let domainTone = 'text-n-slate-11';
  if (pendingDomain.value) {
    domainNote = t('KAPTION_SECURITY.OVERVIEW.TILE_DOMAINS_PENDING', {
      domain: pendingDomain.value.domain,
    });
    domainTone = 'text-n-amber-11';
  } else if (domains.value.length) {
    domainNote = t('KAPTION_SECURITY.OVERVIEW.TILE_DOMAINS_OK');
    domainTone = 'text-n-teal-11';
  }
  list.push({
    key: 'domains',
    label: t('KAPTION_SECURITY.OVERVIEW.TILE_DOMAINS'),
    value: domains.value.length
      ? t('KAPTION_SECURITY.OVERVIEW.TILE_DOMAINS_VALUE', {
          verified: verifiedDomains.value.length,
          total: domains.value.length,
        })
      : t('KAPTION_SECURITY.OVERVIEW.TILE_DOMAINS_NONE'),
    note: domainNote,
    tone: domainTone,
  });

  if (twoStepAvailable.value && twoStep.value) {
    const missing = twoStep.value.required_total - twoStep.value.with_method;
    list.push({
      key: 'two_step',
      label:
        twoStep.value.required_for === 'everyone'
          ? t('KAPTION_SECURITY.OVERVIEW.TILE_TWO_STEP_EVERYONE')
          : t('KAPTION_SECURITY.OVERVIEW.TILE_TWO_STEP_ADMINS'),
      value: t('KAPTION_SECURITY.OVERVIEW.TILE_TWO_STEP_VALUE', {
        with: twoStep.value.with_method,
        total: twoStep.value.required_total,
      }),
      note:
        missing > 0
          ? t('KAPTION_SECURITY.OVERVIEW.TILE_TWO_STEP_MISSING', {
              count: missing,
            })
          : t('KAPTION_SECURITY.OVERVIEW.TILE_TWO_STEP_OK'),
      tone: missing > 0 ? 'text-n-amber-11' : 'text-n-teal-11',
    });
  } else {
    const hours = settings.value.session_max_age_hours;
    list.push({
      key: 'sessions',
      label: t('KAPTION_SECURITY.OVERVIEW.TILE_SESSIONS'),
      value: hours
        ? t('KAPTION_SECURITY.OVERVIEW.TILE_SESSIONS_LIMIT', {
            duration: t(`KAPTION_SECURITY.SESSIONS.HOURS.${hours}`),
          })
        : t('KAPTION_SECURITY.OVERVIEW.TILE_SESSIONS_DEFAULT'),
      note: t('KAPTION_SECURITY.OVERVIEW.TILE_SESSIONS_NOTE'),
      tone: 'text-n-slate-11',
    });
  }
  return list;
});

const policies = computed(() => {
  const vars = { domains: domainsText.value, provider: provider.value };
  return [
    {
      value: 'password',
      title: t('KAPTION_SECURITY.OVERVIEW.POLICY_PASSWORD_TITLE'),
      desc: t('KAPTION_SECURITY.OVERVIEW.POLICY_PASSWORD_DESC'),
      disabled: false,
    },
    {
      value: 'sso_available',
      title: t('KAPTION_SECURITY.OVERVIEW.POLICY_AVAILABLE_TITLE'),
      desc: t('KAPTION_SECURITY.OVERVIEW.POLICY_AVAILABLE_DESC', vars),
      disabled: !ssoReady.value,
    },
    {
      value: 'sso_required',
      title: t('KAPTION_SECURITY.OVERVIEW.POLICY_REQUIRED_TITLE', vars),
      desc: t('KAPTION_SECURITY.OVERVIEW.POLICY_REQUIRED_DESC', vars),
      disabled: !ssoReady.value,
    },
  ];
});

const events = computed(() => overview.value?.recent_events || []);

const choose = async value => {
  if (value === policy.value || busy.value) return;
  if (value === 'sso_required') {
    enforceRef.value?.open();
    return;
  }
  busy.value = true;
  try {
    const { data } = await KaptionSecurityAPI.updatePolicy({
      sign_in_policy: value,
    });
    setOverview(data);
    useAlert(t('KAPTION_SECURITY.COMMON.SAVED'));
  } catch (e) {
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-11">
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div
        v-for="tile in tiles"
        :key="tile.key"
        class="flex flex-col gap-1.5 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
        :data-testid="`ks-tile-${tile.key}`"
      >
        <span class="text-[13px] text-n-slate-11">{{ tile.label }}</span>
        <span class="text-lg font-semibold text-n-slate-12">{{
          tile.value
        }}</span>
        <span class="text-[13px]" :class="tile.tone">{{ tile.note }}</span>
      </div>
    </div>

    <KaptionSecuritySection
      :title="t('KAPTION_SECURITY.OVERVIEW.POLICY_TITLE')"
    >
      <fieldset class="flex flex-col p-0 m-0 border rounded-xl border-n-weak">
        <legend class="sr-only">
          {{ t('KAPTION_SECURITY.OVERVIEW.POLICY_TITLE') }}
        </legend>
        <label
          v-for="(item, index) in policies"
          :key="item.value"
          class="flex gap-3 p-4"
          :class="[
            index > 0 ? 'border-t border-n-weak' : '',
            item.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          ]"
          :data-testid="`ks-policy-${item.value}`"
        >
          <input
            type="radio"
            name="ks-policy"
            class="mt-1 accent-n-brand"
            :value="item.value"
            :checked="policy === item.value"
            :disabled="item.disabled || busy"
            @change="choose(item.value)"
          />
          <span class="flex flex-col gap-1">
            <span class="text-sm font-semibold text-n-slate-12">{{
              item.title
            }}</span>
            <span class="text-sm text-n-slate-11">{{ item.desc }}</span>
          </span>
        </label>
      </fieldset>
      <p v-if="!ssoReady" class="m-0 text-[13px] text-n-slate-11">
        {{ t('KAPTION_SECURITY.OVERVIEW.POLICY_NEEDS_SETUP') }}
      </p>
    </KaptionSecuritySection>

    <KaptionSecuritySection
      :title="t('KAPTION_SECURITY.OVERVIEW.RECENT_TITLE')"
    >
      <template #actions>
        <router-link
          :to="{
            name: 'settings_kaption_security_activity',
            params: { accountId },
          }"
          class="text-sm font-medium text-n-brand"
        >
          {{ t('KAPTION_SECURITY.OVERVIEW.SEE_ALL') }}
        </router-link>
      </template>
      <p v-if="!events.length" class="m-0 text-sm text-n-slate-11">
        {{ t('KAPTION_SECURITY.OVERVIEW.NO_EVENTS') }}
      </p>
      <div v-else class="flex flex-col">
        <div
          v-for="(event, index) in events"
          :key="event.id"
          class="grid grid-cols-[110px_minmax(0,1fr)] sm:grid-cols-[120px_minmax(0,1fr)_160px] gap-x-4 gap-y-1 py-2.5 text-sm"
          :class="{ 'border-t border-n-weak': index > 0 }"
        >
          <span class="text-n-slate-11">{{
            formatWhen(event.created_at)
          }}</span>
          <span class="text-n-slate-12">{{ eventText(event) }}</span>
          <span
            class="col-start-2 sm:col-start-auto text-n-slate-11 sm:text-right"
            >{{ eventWho(event) }}</span
          >
        </div>
      </div>
    </KaptionSecuritySection>

    <KaptionSecuritySection :title="t('KAPTION_SECURITY.OVERVIEW.SOON_TITLE')">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <KaptionSecuritySoonCard
          icon="i-lucide-network"
          :title="t('KAPTION_SECURITY.OVERVIEW.IP_TITLE')"
          :description="t('KAPTION_SECURITY.OVERVIEW.IP_DESC')"
        />
        <KaptionSecuritySoonCard
          icon="i-lucide-key-round"
          :title="t('KAPTION_SECURITY.OVERVIEW.TOKENS_TITLE')"
          :description="t('KAPTION_SECURITY.OVERVIEW.TOKENS_DESC')"
        />
      </div>
    </KaptionSecuritySection>

    <KaptionSecurityEnforceDialog ref="enforceRef" />
  </div>
</template>
